<template>
  <v-container fluid class="pa-0 fill-height">
    <!-- 🟢 Header -->
    <v-app-bar app color="primary" dark flat>
      <v-btn icon @click="$router.back()">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-toolbar-title class="font-weight-bold">Edit Profile</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn
        color="secondary"
        rounded
        small
        :disabled="age && age < 17"
        @click="onFinish()"
      >
        Save Changes
      </v-btn>
    </v-app-bar>

    <!-- 🟢 Glass 'Match Strength' Bar -->
    <v-container grid-list-lg class="progress-bar-floating">
      <v-card class="glass-bar rounded-xl">
        <v-card-text>
          <v-progress-linear
            :value="completionPercent"
            height="10"
            :color="progressColor"
            rounded
            striped
            stream
          ></v-progress-linear>

          <div class="d-flex justify-space-between align-center mt-1 px-4 pb-2">
            <span class="text-caption grey--text text--darken-1">
              {{ progressMessage }}
            </span>
            <span
              class="font-weight-bold"
              :class="{
                'red--text': completionPercent < 30,
                'orange--text':
                  completionPercent >= 30 && completionPercent < 70,
                'green--text': completionPercent >= 70,
              }"
            >
              {{ completionPercent }}%
            </span>
          </div>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- 🟢 Success Screen -->
    <v-container v-if="submitted" class="py-10 px-6" style="margin-top: 120px">
      <div class="text-center my-12">
        <v-icon color="green" size="72">mdi-check-circle-outline</v-icon>
        <h2 class="font-weight-bold mt-4">Alhamdulillah! 🎉</h2>
        <p class="mt-2 text-body-1">
          Your profile has been updated successfully.
        </p>
        <p class="text-body-2 grey--text">
          Your updates have been saved. JazakAllah Khair!
        </p>
        <v-btn
          color="primary"
          class="mt-6"
          rounded
          large
          @click="$router.push('/user/profile')"
        >
          Go to My Profile
        </v-btn>
      </div>
    </v-container>

    <!-- 🟢 Stepper Wizard -->
    <v-container v-else class="py-4 px-4" style="margin-top: 120px !important">
      <v-sheet color="primary" dark rounded="xl" class="mb-6 pa-0">
        <v-stepper v-model="step" class="stepper-nonclickable" flat>
          <v-stepper-header>
            <v-stepper-step :complete="step > 1" step="1"
              >Basic Info</v-stepper-step
            >
            <v-divider></v-divider>
            <v-stepper-step :complete="step > 2" step="2"
              >Personal</v-stepper-step
            >
            <v-divider></v-divider>
            <v-stepper-step :complete="step > 3" step="3">Faith</v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :complete="step > 4" step="4"
              >Family</v-stepper-step
            >
            <v-divider></v-divider>
            <v-stepper-step :complete="step > 5" step="5"
              >Lifestyle</v-stepper-step
            >
            <v-divider></v-divider>
            <v-stepper-step :complete="step > 6" step="6"
              >Career</v-stepper-step
            >
            <v-divider></v-divider>
            <v-stepper-step :complete="step > 7" step="7">
              Marriage Expectations
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :complete="step > 8" :editable="false" step="8">
              About
            </v-stepper-step>
          </v-stepper-header>
        </v-stepper>
      </v-sheet>

      <!-- 🔹 Exact same stepper items as Create -->
      <v-stepper v-model="step" class="elevation-0">
        <v-stepper-items>
          <!-- STEP 1: Basic Information -->
          <v-stepper-content step="1">
            <v-subheader class="text--primary font-weight-bold"
              >Basic Information</v-subheader
            >

            <v-text-field
              v-model="profile.name"
              label="Full Name *"
              outlined
              autofocus
              dense
              color="primary"
              @input="
                $v.profile.name.$touch();
                updateProgress();
              "
              :error-messages="nameErrors()"
            ></v-text-field>

            <v-select
              v-model="profile.gender"
              :items="genderOptions"
              label="Gender *"
              outlined
              dense
              color="primary"
              :menu-props="{ offsetY: true }"
              @change="
                $v.profile.gender.$touch();
                updateProgress();
                handleGenderChange();
              "
              :error-messages="genderErrors()"
            ></v-select>

            <v-row>
              <v-col cols="4">
                <v-select
                  v-model="dobMonth"
                  :items="months"
                  label="Month *"
                  outlined
                  dense
                  color="primary"
                  :menu-props="{ offsetY: true }"
                  :error-messages="dobErrors()"
                  @change="
                    updateDob();
                    $v.profile.dob.$touch();
                    updateProgress();
                  "
                ></v-select>
              </v-col>

              <v-col cols="4">
                <v-select
                  v-model="dobDay"
                  :items="days"
                  label="Day"
                  outlined
                  dense
                  color="primary"
                  :menu-props="{ offsetY: true }"
                  @change="
                    updateDob();
                    $v.profile.dob.$touch();
                    updateProgress();
                  "
                ></v-select>
              </v-col>

              <v-col cols="4">
                <v-select
                  v-model="dobYear"
                  :items="years"
                  label="Year"
                  outlined
                  dense
                  color="primary"
                  :menu-props="{ offsetY: true }"
                  @change="
                    updateDob();
                    $v.profile.dob.$touch();
                    updateProgress();
                  "
                ></v-select>
              </v-col>
            </v-row>

            <div class="mb-4">
              <div
                v-if="age !== ''"
                class="text-caption mb-1 ml-1"
                :class="ageColor"
              >
                Age: <strong>{{ age }} years old</strong>
              </div>
              <div
                v-if="age !== '' && age < 17"
                class="error--text text-caption ml-1"
              >
                Minimum marriage age is 17 years old.
              </div>
            </div>

            <v-select
              v-model="profile.country"
              :items="countries"
              label="Country *"
              outlined
              dense
              color="primary"
              :menu-props="{ offsetY: true }"
              @change="
                $v.profile.country.$touch();
                handleCountryChange();
                updateProgress();
              "
              :error-messages="countryErrors()"
            ></v-select>

            <v-select
              v-model="profile.state"
              :items="availableStates"
              label="State / Province *"
              outlined
              dense
              color="primary"
              :disabled="!profile.country"
              :menu-props="{ offsetY: true }"
              @change="
                $v.profile.state.$touch();
                updateProgress();
              "
              :error-messages="stateErrors()"
            ></v-select>

            <v-text-field
              v-model="profile.city"
              label="City *"
              outlined
              dense
              color="primary"
              @input="
                $v.profile.city.$touch();
                updateProgress();
              "
              :error-messages="cityErrors()"
            ></v-text-field>

            <!-- 👨‍👩‍👧 Relation to the Profile -->
            <v-select
              v-model="profile.relationToUser"
              :items="relationOptions"
              label="Your Relationship to This Person *"
              outlined
              dense
              color="primary"
              :menu-props="{ offsetY: true }"
              @change="
                $v.profile.relationToUser.$touch();
                updateProgress();
              "
              :error-messages="relationErrors()"
            />

            <v-slide-y-transition>
              <v-text-field
                v-if="profile.relationToUser === 'Other'"
                v-model="relationOther"
                label="Please specify your relationship"
                outlined
                dense
                color="primary"
                :error-messages="relationOtherErrors()"
                @input="
                  $v.relationOther.$touch();
                  updateProgress();
                "
              />
            </v-slide-y-transition>

            <div class="d-flex justify-space-between mt-6">
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                rounded
                large
                :disabled="!canProceedStep(1)"
                @click="nextStep()"
              >
                Next
              </v-btn>
            </div>
          </v-stepper-content>

          <!-- STEP 2: Personal Details -->
          <v-stepper-content step="2">
            <v-subheader class="text--primary font-weight-bold"
              >Personal Details</v-subheader
            >

            <v-select
              v-model="profile.maritalStatus"
              :items="maritalStatusOptions"
              label="Marital Status *"
              outlined
              dense
              color="primary"
              :menu-props="{ offsetY: true }"
              @change="
                $v.profile.maritalStatus.$touch();
                updateProgress();
              "
              :error-messages="
                !$v.profile.maritalStatus.$dirty
                  ? []
                  : !$v.profile.maritalStatus.required
                  ? ['Marital Status is required']
                  : []
              "
            ></v-select>

            <!-- 🔹 Appearance -->
            <v-select
              v-model="profile.appearance"
              :items="appearanceOptions"
              label="Appearance"
              outlined
              dense
              color="primary"
              @change="updateProgress"
            ></v-select>

            <!-- 🔹 Children -->
            <div class="text--primary font-weight-bold mt-2">
              Do you have children?
            </div>
            <v-radio-group
              v-model="profile.hasChildren"
              row
              @change="updateProgress"
            >
              <v-radio label="Yes" value="yes"></v-radio>
              <v-radio label="No" value="no"></v-radio>
            </v-radio-group>

            <div class="text--primary font-weight-bold mt-2">
              Would you like to have children in the future?
            </div>
            <v-radio-group
              v-model="profile.wantsChildren"
              row
              @change="updateProgress"
            >
              <v-radio label="Yes" value="yes"></v-radio>
              <v-radio label="No" value="no"></v-radio>
              <v-radio label="Maybe" value="unspecified"></v-radio>
            </v-radio-group>

            <v-select
              v-model="profile.educationLevel"
              :items="educationOptions"
              label="Education"
              outlined
              dense
              color="primary"
              :menu-props="{ offsetY: true }"
              @change="updateProgress"
            ></v-select>

            <v-select
              v-model="profile.legalStatus"
              :items="legalStatusOptions"
              label="Legal Status / Citizenship *"
              outlined
              dense
              color="primary"
              :menu-props="{ offsetY: true }"
              @change="
                $v.profile.legalStatus.$touch();
                updateProgress();
              "
              :error-messages="
                !$v.profile.legalStatus.$dirty
                  ? []
                  : !$v.profile.legalStatus.required
                  ? ['Legal status is required']
                  : []
              "
            ></v-select>

            <v-row>
              <v-col cols="6">
                <v-select
                  v-model="profile.heightFeet"
                  :items="feetOptions"
                  label="Height (ft)"
                  outlined
                  dense
                  color="primary"
                  :menu-props="{ offsetY: true }"
                  @change="updateProgress"
                ></v-select>
              </v-col>
              <v-col cols="6">
                <v-select
                  v-model="profile.heightInches"
                  :items="inchOptions"
                  label="Height (in)"
                  outlined
                  dense
                  color="primary"
                  :menu-props="{ offsetY: true }"
                  @change="updateProgress"
                ></v-select>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model="profile.weightLbs"
                  label="Weight (lbs)"
                  type="number"
                  outlined
                  dense
                  color="primary"
                  hide-details
                  @input="updateProgress"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-select
                  v-model="profile.build"
                  :items="buildOptions"
                  label="Body Type"
                  outlined
                  dense
                  color="primary"
                  :menu-props="{ offsetY: true }"
                  hide-details
                  @change="updateProgress"
                ></v-select>
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="profile.complexion"
                  :items="complexionOptions"
                  label="Complexion"
                  outlined
                  dense
                  color="primary"
                  :menu-props="{ offsetY: true }"
                  @change="updateProgress"
                ></v-select>
              </v-col>
            </v-row>

            <div class="d-flex justify-space-between mt-6">
              <v-btn text rounded large @click="prevStep()">Back</v-btn>
              <v-btn
                color="primary"
                rounded
                large
                :disabled="!canProceedStep(2)"
                @click="nextStep()"
                >Next</v-btn
              >
            </div>
          </v-stepper-content>

          <!-- STEP 3: Faith & Religious Practice -->
          <v-stepper-content step="3">
            <v-subheader class="text--primary font-weight-bold"
              >Faith & Religious Practice</v-subheader
            >

            <v-select
              v-model="profile.madhab"
              :items="madhabOptions"
              label="Madhab / School of Thought *"
              outlined
              dense
              color="primary"
              :menu-props="{ offsetY: true }"
              @change="
                $v.profile.madhab.$touch();
                updateProgress();
              "
              :error-messages="
                !$v.profile.madhab.$dirty
                  ? []
                  : !$v.profile.madhab.required
                  ? ['Madhab is required']
                  : []
              "
            />

            <v-select
              v-model="profile.prayerFrequency"
              :items="prayerOptions"
              label="Prayer Habit"
              outlined
              dense
              color="primary"
              :menu-props="{ offsetY: true }"
              @change="updateProgress"
            />

            <div class="text--primary font-weight-bold mt-2">
              Do you fast in Ramadan?
            </div>
            <v-radio-group
              v-model="profile.fastInRamadan"
              :mandatory="false"
              row
              @change="updateProgress"
            >
              <v-radio label="Yes" value="yes"></v-radio>
              <v-radio label="No" value="no"></v-radio>
              <v-radio label="Prefer not to say" value="unspecified"></v-radio>
            </v-radio-group>

            <div class="text--primary font-weight-bold mt-1">
              Do you give Zakat annually?
            </div>
            <v-radio-group
              v-model="profile.givesZakat"
              :mandatory="false"
              row
              @change="updateProgress"
            >
              <v-radio label="Yes" value="yes"></v-radio>
              <v-radio label="No" value="no"></v-radio>
              <v-radio label="Prefer not to say" value="unspecified"></v-radio>
            </v-radio-group>

            <v-text-field
              v-model="profile.masjidName"
              label="Masjid you usually attend (optional)"
              outlined
              dense
              color="primary"
              placeholder="e.g., Islamic Center of Long Island"
              @input="updateProgress"
            />

            <v-text-field
              v-model="profile.masjidAddress"
              label="Masjid Address"
              outlined
              dense
              color="primary"
              placeholder="Street, City, State"
              @input="updateProgress"
            />

            <v-select
              v-model="profile.islamicEducationLevel"
              :items="[
                'None',
                'Basic',
                'Intermediate',
                'Advanced',
                'Hafiz',
                'Alim/Scholar',
              ]"
              label="Islamic Education Level"
              outlined
              dense
              color="primary"
              @change="updateProgress"
            />

            <div class="text--primary font-weight-bold mt-2">
              Do you pray in Jamaat?
            </div>
            <v-radio-group
              v-model="profile.praysInJamaat"
              row
              @change="updateProgress"
            >
              <v-radio label="Yes" value="yes"></v-radio>
              <v-radio label="No" value="no"></v-radio>
            </v-radio-group>

            <div class="text--primary font-weight-bold mt-2">
              Have you performed Hajj?
            </div>
            <v-radio-group
              v-model="profile.hajjStatus"
              row
              @change="updateProgress"
            >
              <v-radio label="Yes" value="yes"></v-radio>
              <v-radio label="No" value="no"></v-radio>
            </v-radio-group>

            <div class="text--primary font-weight-bold mt-2">
              Have you performed Umrah?
            </div>
            <v-radio-group
              v-model="profile.umrahStatus"
              row
              @change="updateProgress"
            >
              <v-radio label="Yes" value="yes"></v-radio>
              <v-radio label="No" value="no"></v-radio>
            </v-radio-group>

            <div class="d-flex justify-space-between mt-6">
              <v-btn text rounded large @click="prevStep()">Back</v-btn>
              <v-btn
                color="primary"
                rounded
                large
                :disabled="!canProceedStep(3)"
                @click="nextStep()"
                >Next</v-btn
              >
            </div>
          </v-stepper-content>

          <!-- STEP 4: Family & Background -->
          <v-stepper-content step="4">
            <v-subheader class="text--primary font-weight-bold"
              >Family & Background</v-subheader
            >

            <v-select
              v-model="profile.ethnicity"
              :items="ethnicityOptions"
              label="Ethnicity / National Origin *"
              outlined
              dense
              color="primary"
              :menu-props="{ offsetY: true }"
              @change="
                $v.profile.ethnicity.$touch();
                updateProgress();
              "
              :error-messages="
                !$v.profile.ethnicity.$dirty
                  ? []
                  : !$v.profile.ethnicity.required
                  ? ['Ethnicity is required']
                  : []
              "
            />

            <v-text-field
              v-model="profile.innerCaste"
              label="Inner Caste / Community (optional)"
              outlined
              dense
              color="primary"
              placeholder="e.g., Punjabi, Pathan, Memon, Baloch"
              @input="updateProgress"
            />

            <div class="text--primary font-weight-bold">
              Do you have any siblings?
            </div>
            <v-radio-group
              v-model="profile.hasSiblings"
              :mandatory="false"
              row
              @change="updateProgress"
            >
              <v-radio label="Yes" value="yes"></v-radio>
              <v-radio label="No" value="no"></v-radio>
              <v-radio label="Prefer not to say" value="unspecified"></v-radio>
            </v-radio-group>

            <v-slide-y-transition>
              <div v-if="profile.hasSiblings === 'yes'">
                <v-row>
                  <v-col cols="6">
                    <v-select
                      v-model="profile.brotherCount"
                      :items="brotherCountOptions"
                      label="Number of Brothers"
                      outlined
                      dense
                      color="primary"
                      :menu-props="{ offsetY: true }"
                      @change="updateProgress"
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-select
                      v-model="profile.sisterCount"
                      :items="sisterCountOptions"
                      label="Number of Sisters"
                      outlined
                      dense
                      color="primary"
                      :menu-props="{ offsetY: true }"
                      @change="updateProgress"
                    />
                  </v-col>
                </v-row>

                <v-select
                  v-model="profile.siblingOrder"
                  :items="siblingOrderOptions"
                  label="Sibling Order"
                  outlined
                  dense
                  color="primary"
                  :menu-props="{ offsetY: true }"
                  @change="updateProgress"
                />
              </div>
            </v-slide-y-transition>

            <div class="text--primary font-weight-bold mt-2">
              Is your father alive?
            </div>
            <v-radio-group
              v-model="profile.fatherAlive"
              :mandatory="false"
              row
              @change="updateProgress"
            >
              <v-radio label="Yes" value="yes"></v-radio>
              <v-radio label="No" value="no"></v-radio>
              <v-radio label="Prefer not to say" value="unspecified"></v-radio>
            </v-radio-group>

            <v-slide-y-transition>
              <div v-if="profile.fatherAlive === 'yes'">
                <v-text-field
                  v-model="profile.fatherLocation"
                  label="Father’s Location"
                  outlined
                  dense
                  color="primary"
                  placeholder="e.g., US or Back Home"
                  @input="updateProgress"
                />
              </div>
            </v-slide-y-transition>

            <div class="text--primary font-weight-bold mt-2">
              Is your mother alive?
            </div>
            <v-radio-group
              v-model="profile.motherAlive"
              :mandatory="false"
              row
              @change="updateProgress"
            >
              <v-radio label="Yes" value="yes"></v-radio>
              <v-radio label="No" value="no"></v-radio>
              <v-radio label="Prefer not to say" value="unspecified"></v-radio>
            </v-radio-group>

            <v-slide-y-transition>
              <div v-if="profile.motherAlive === 'yes'">
                <v-text-field
                  v-model="profile.motherLocation"
                  label="Mother’s Location"
                  outlined
                  dense
                  color="primary"
                  placeholder="e.g., US or Back Home"
                  @input="updateProgress"
                />
              </div>
            </v-slide-y-transition>

            <div class="d-flex justify-space-between mt-6">
              <v-btn text rounded large @click="prevStep()">Back</v-btn>
              <v-btn
                color="primary"
                rounded
                large
                :disabled="!canProceedStep(4)"
                @click="nextStep()"
                >Next</v-btn
              >
            </div>
          </v-stepper-content>

          <!-- STEP 5: Lifestyle & Preferences -->
          <v-stepper-content step="5">
            <v-subheader class="text--primary font-weight-bold"
              >Lifestyle & Preferences</v-subheader
            >

            <v-select
              v-model="profile.personalityType"
              :items="personalityTypeOptions"
              label="How would you describe yourself?"
              outlined
              dense
              color="primary"
              :menu-props="{ offsetY: true }"
              @change="updateProgress"
            />

            <v-select
              v-model="profile.dietType"
              :items="dietTypeOptions"
              label="Diet Type"
              outlined
              dense
              color="primary"
              :menu-props="{ offsetY: true }"
              @change="updateProgress"
            />

            <v-select
              v-model="profile.foodPreference"
              :items="foodPreferenceOptions"
              label="Food Preference"
              outlined
              dense
              color="primary"
              :menu-props="{ offsetY: true }"
              @change="updateProgress"
            />

            <!-- Men only -->
            <v-slide-y-transition>
              <div v-if="profile.gender === 'Male'">
                <v-select
                  v-model="profile.livingArrangement"
                  :items="livingArrangementOptions"
                  label="Post-Marriage Living Arrangement"
                  outlined
                  dense
                  color="primary"
                  :menu-props="{ offsetY: true }"
                  @change="updateProgress"
                />
              </div>
            </v-slide-y-transition>

            <!-- Women only -->
            <v-slide-y-transition>
              <div v-if="profile.gender === 'Female'">
                <v-select
                  v-model="profile.postMarriageWork"
                  :items="postMarriageWorkOptions"
                  label="After marriage, would you like to work?"
                  outlined
                  dense
                  color="primary"
                  :menu-props="{ offsetY: true }"
                  @change="updateProgress"
                />
              </div>
            </v-slide-y-transition>

            <div class="text--primary font-weight-bold">
              Do you like to travel?
            </div>
            <v-radio-group
              v-model="profile.travelLover"
              :mandatory="false"
              row
              @change="updateProgress"
            >
              <v-radio label="Yes" value="yes"></v-radio>
              <v-radio label="No" value="no"></v-radio>
              <v-radio label="Prefer not to say" value="unspecified"></v-radio>
            </v-radio-group>

            <v-slide-y-transition>
              <div v-if="profile.travelLover === 'yes'">
                <v-select
                  v-model="profile.travelFrequency"
                  :items="travelFrequencyOptions"
                  label="How often do you travel?"
                  outlined
                  dense
                  color="primary"
                  :menu-props="{ offsetY: true }"
                  @change="updateProgress"
                />
              </div>
            </v-slide-y-transition>

            <div class="text--primary font-weight-bold mt-2">
              Do you enjoy hosting guests?
            </div>
            <v-radio-group
              v-model="profile.hostGuests"
              row
              @change="updateProgress"
            >
              <v-radio label="Yes" value="yes"></v-radio>
              <v-radio label="No" value="no"></v-radio>
            </v-radio-group>

            <v-select
              v-model="profile.homeAtmosphere"
              :items="[
                'Quiet and Peaceful',
                'Active and Social',
                'Religious Environment',
                'Family-Oriented',
              ]"
              label="Home Atmosphere"
              outlined
              dense
              color="primary"
              @change="updateProgress"
            />

            <v-select
              v-model="profile.cookingSkill"
              :items="['Beginner', 'Intermediate', 'Good', 'Excellent']"
              label="Cooking Skill"
              outlined
              dense
              color="primary"
              @change="updateProgress"
            />

            <div class="text--primary font-weight-bold mt-2">
              Have you done pre-marriage counseling?
            </div>
            <v-radio-group
              v-model="profile.preMarriageCounseling"
              row
              @change="updateProgress"
            >
              <v-radio label="Yes" value="yes"></v-radio>
              <v-radio label="No" value="no"></v-radio>
            </v-radio-group>

            <!-- 🔹 Health & Habits -->
            <v-divider class="my-4"></v-divider>
            <v-subheader class="text--primary font-weight-bold"
              >Health & Habits</v-subheader
            >

            <div class="text--primary font-weight-bold mt-1">
              Do you have any disability?
            </div>
            <v-radio-group
              v-model="profile.hasDisability"
              row
              @change="updateProgress"
            >
              <v-radio label="Yes" value="yes"></v-radio>
              <v-radio label="No" value="no"></v-radio>
            </v-radio-group>

            <v-slide-y-transition>
              <v-text-field
                v-if="profile.hasDisability === 'yes'"
                v-model="profile.disabilityDetail"
                label="Disability Details"
                outlined
                dense
                color="primary"
                @input="updateProgress"
              />
            </v-slide-y-transition>

            <div class="text--primary font-weight-bold mt-2">Do you smoke?</div>
            <v-radio-group
              v-model="profile.smokes"
              row
              @change="updateProgress"
            >
              <v-radio label="Yes" value="yes"></v-radio>
              <v-radio label="No" value="no"></v-radio>
            </v-radio-group>

            <div class="text--primary font-weight-bold mt-2">
              Do you drink alcohol?
            </div>
            <v-radio-group
              v-model="profile.drinks"
              row
              @change="updateProgress"
            >
              <v-radio label="Yes" value="yes"></v-radio>
              <v-radio label="No" value="no"></v-radio>
            </v-radio-group>

            <div class="d-flex justify-space-between mt-6">
              <v-btn text rounded large @click="prevStep()">Back</v-btn>
              <v-btn
                color="primary"
                rounded
                large
                :disabled="!canProceedStep(5)"
                @click="nextStep()"
                >Next</v-btn
              >
            </div>
          </v-stepper-content>

          <!-- STEP 6: Career & Transportation -->
          <v-stepper-content step="6">
            <v-subheader class="text--primary font-weight-bold"
              >Career & Transportation</v-subheader
            >

            <v-select
              v-model="profile.occupationType"
              :items="occupationTypeOptions"
              label="Occupation Type"
              outlined
              dense
              color="primary"
              :menu-props="{ offsetY: true }"
              @change="updateProgress"
            />

            <v-text-field
              v-model="profile.profession"
              label="Profession / Job Title"
              outlined
              dense
              color="primary"
              placeholder="e.g., Software Engineer, Teacher, Business Owner"
              @input="updateProgress"
            />

            <v-select
              v-model="profile.employmentStatus"
              :items="employmentStatusOptions"
              label="Employment Status"
              outlined
              dense
              color="primary"
              :menu-props="{ offsetY: true }"
              @change="updateProgress"
            />

            <div class="text--primary font-weight-bold mt-2">
              Do you know how to drive?
            </div>
            <v-radio-group
              v-model="profile.canDrive"
              :mandatory="false"
              row
              @change="updateProgress"
            >
              <v-radio label="Yes" value="yes"></v-radio>
              <v-radio label="No" value="no"></v-radio>
              <v-radio label="Prefer not to say" value="unspecified"></v-radio>
            </v-radio-group>

            <div class="text--primary font-weight-bold mt-1">
              Do you own a car?
            </div>
            <v-radio-group
              v-model="profile.hasCar"
              :mandatory="false"
              row
              @change="updateProgress"
            >
              <v-radio label="Yes" value="yes"></v-radio>
              <v-radio label="No" value="no"></v-radio>
              <v-radio label="Prefer not to say" value="unspecified"></v-radio>
            </v-radio-group>

            <v-slide-y-transition>
              <div v-if="profile.hasCar === 'yes'">
                <v-select
                  v-model="profile.vehicleType"
                  :items="vehicleTypeOptions"
                  label="Type of Vehicle"
                  outlined
                  dense
                  color="primary"
                  :menu-props="{ offsetY: true }"
                  @change="updateProgress"
                />
              </div>
            </v-slide-y-transition>

            <v-select
              v-model="profile.workEnvironment"
              :items="['Office', 'Field', 'Remote', 'Hybrid']"
              label="Work Environment"
              outlined
              dense
              color="primary"
              @change="updateProgress"
            />

            <div class="text--primary font-weight-bold mt-2">
              Does your job require travel?
            </div>
            <v-radio-group
              v-model="profile.travelForWork"
              row
              @change="updateProgress"
            >
              <v-radio label="Yes" value="yes"></v-radio>
              <v-radio label="No" value="no"></v-radio>
            </v-radio-group>

            <div class="text--primary font-weight-bold mt-2">
              Do you own a motorbike?
            </div>
            <v-radio-group
              v-model="profile.hasMotorbike"
              row
              @change="updateProgress"
            >
              <v-radio label="Yes" value="yes"></v-radio>
              <v-radio label="No" value="no"></v-radio>
            </v-radio-group>

            <div class="d-flex justify-space-between mt-6">
              <v-btn text rounded large @click="prevStep()">Back</v-btn>
              <v-btn
                color="primary"
                rounded
                large
                :disabled="!canProceedStep(6)"
                @click="nextStep()"
                >Next</v-btn
              >
            </div>
          </v-stepper-content>

          <!-- STEP 7: Marriage Expectations -->
          <v-stepper-content step="7">
            <v-subheader class="text--primary font-weight-bold">
              Marriage Expectations
            </v-subheader>

            <div class="text--primary font-weight-bold">
              Was Mehr discussed or considered?
            </div>
            <v-radio-group
              v-model="profile.mehrConsidered"
              row
              @change="updateProgress"
            >
              <v-radio label="Yes" value="yes"></v-radio>
              <v-radio label="No" value="no"></v-radio>
            </v-radio-group>

            <v-text-field
              v-model="profile.mehrAmount"
              label="Mehr Amount or Form"
              outlined
              dense
              color="primary"
              @input="updateProgress"
            />

            <v-textarea
              v-model="profile.mehrNotes"
              label="Mehr Notes"
              outlined
              dense
              color="primary"
              rows="3"
              @input="updateProgress"
            />

            <v-select
              v-model="profile.wifeWorkPreference"
              :items="[
                'Can work',
                'Can work with conditions',
                'Prefer not to work',
              ]"
              label="Wife Work Preference"
              outlined
              dense
              color="primary"
              @change="updateProgress"
            />

            <v-select
              v-model="profile.financialResponsibility"
              :items="[
                'Husband only',
                'Shared equally',
                'Flexible depending on situation',
              ]"
              label="Financial Responsibility"
              outlined
              dense
              color="primary"
              @change="updateProgress"
            />

            <v-select
              v-model="profile.domesticContribution"
              :items="[
                'Shared equally',
                'Husband helps when needed',
                'Traditional roles',
              ]"
              label="Domestic Contribution"
              outlined
              dense
              color="primary"
              @change="updateProgress"
            />

            <div class="text--primary font-weight-bold mt-2">
              Do you support education after marriage?
            </div>
            <v-radio-group
              v-model="profile.supportsEducation"
              row
              @change="updateProgress"
            >
              <v-radio label="Yes" value="yes"></v-radio>
              <v-radio label="No" value="no"></v-radio>
            </v-radio-group>

            <div class="d-flex justify-space-between mt-6">
              <v-btn text rounded large @click="prevStep()">Back</v-btn>
              <v-btn
                color="primary"
                rounded
                large
                :disabled="!canProceedStep(7)"
                @click="nextStep()"
                >Next</v-btn
              >
            </div>
          </v-stepper-content>

          <!-- STEP 8: About & Submit -->
          <v-stepper-content step="8">
            <v-subheader class="text--primary font-weight-bold"
              >About Yourself</v-subheader
            >

            <v-textarea
              v-model="profile.bio"
              label="Tell us something about yourself"
              outlined
              dense
              color="primary"
              counter="500"
              auto-grow
              maxlength="500"
              rows="5"
              placeholder="Write a short introduction (e.g., your interests, personality, or goals)..."
              @input="updateProgress"
            ></v-textarea>

            <div class="d-flex justify-space-between mt-6">
              <v-btn text rounded large @click="prevStep()">Back</v-btn>
              <v-btn
                color="primary"
                rounded
                large
                :disabled="saving"
                :loading="saving"
                @click="onFinish()"
                >Update
              </v-btn>
            </div>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </v-container>
  </v-container>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required, minLength } from "vuelidate/lib/validators";
import profileService from "@/modules/Profile/service";
import profileOptions from "@/modules/Profile/config/profileOptions";

export default {
  mixins: [validationMixin],
  data() {
    return {
      step: 1,
      totalSteps: 8, // ✅ match Create Profile steps
      submitted: false,
      saving: false,
      ...profileOptions,
      appearanceOptions: [],
      languagesSpoken: [],
      dobMonth: null,
      dobDay: null,
      dobYear: null,
      days: Array.from({ length: 31 }, (_, i) => i + 1),
      years: [],
      relationOther: "",
      // ✅ initialize full profile with default keys (copy from Create)
      profile: {
        name: "",
        gender: "",
        dob: "",
        country: "",
        state: "",
        city: "",
        relationToUser: "",
        appearance: "",
        heightFeet: null,
        heightInches: null,
        maritalStatus: "",
        educationLevel: "",
        madhab: "",
        legalStatus: "",
        weightLbs: "",
        build: "",
        complexion: "",
        smokes: null,
        drinks: null,
        hasChildren: null,
        bio: "",
        childrenCount: null,
        siblingCount: null,
        parentsLiving: null,
        fatherLocation: "",
        motherLocation: "",
        prayWithJamaat: null,
        fastInRamadan: null,
        hasDisability: null,
        disabilityDetail: "",
        ethnicity: "",
        innerCaste: "",
        isRevert: null,
        fatherAlive: null,
        motherAlive: null,
        wantsChildren: null,
        livingArrangement: "",
        personalityType: "",
        hasSiblings: null,
        brotherCount: null,
        sisterCount: null,
        siblingOrder: "",
        weekendActivity: "",
        eatOutFrequency: "",
        foodPreference: "",
        dietType: "",
        travelLover: null,
        livingPreference: "",
        timePreference: "",
        hostGuests: null,
        homeAtmosphere: "",
        masjidName: null,
        masjidAddress: null,
        islamicEducationLevel: "",
        prayerFrequency: "",
        praysInJamaat: null,
        givesZakat: null,
        occupationType: "",
        profession: "",
        employmentStatus: "",
        cookingSkill: "",
        preMarriageCounseling: "",
        workEnvironment: "",
        travelForWork: null,
        travelFrequency: "",
        canDrive: null,
        hasCar: null,
        vehicleType: "",
        hasMotorbike: null,
        postMarriageWork: "",
        mehrConsidered: "",
        mehrAmount: "",
        mehrNotes: "",
        wifeWorkPreference: "",
        financialResponsibility: "",
        domesticContribution: "",
        supportsEducation: null,
        hajjStatus: "",
        umrahStatus: "",
      },
      availableStates: [],
      inchOptions: Array.from({ length: 12 }, (_, i) => i),
      totalFields: 40,
      completionPercent: 0,
    };
  },
  async created() {
    try {
      const id =
        this.$route.params.id ||
        this.$route.params.profileId ||
        sessionStorage.getItem("lastProfileId");

      if (!id) {
        this.$swal({
          icon: "error",
          title: "Profile Not Found",
          text: "Please select a valid profile to edit.",
        }).then(() => this.$router.push({ name: "user-profile" }));
        return;
      }

      sessionStorage.setItem("lastProfileId", id);
      const result = await profileService.getById(id);
      if (!result || !result.data) throw new Error("Invalid profile data");
      const fetched = result.data;

      // ✅ Merge safely into initialized profile (avoids missing keys)
      this.profile = { ...this.profile, ...fetched };

      // ✅ Parse DOB only if valid
      if (this.profile.dob) {
        const d = new Date(this.profile.dob);
        if (!isNaN(d.getTime())) {
          this.dobYear = d.getFullYear();
          this.dobMonth = d.getMonth() + 1;
          this.dobDay = d.getDate();
        }
      }

      const currentYear = new Date().getFullYear();
      this.years = Array.from({ length: 80 }, (_, i) => currentYear - i);

      if (this.profile.country) this.handleCountryChange(false);
      if (this.profile.gender) this.handleGenderChange(false);
      this.updateProgress();
    } catch (err) {
      console.error("Error loading profile", err);
      this.$swal({
        icon: "error",
        title: "Unable to Load Profile",
        text: "Profile could not be found or may have been removed.",
      }).then(() => this.$router.push({ name: "profile-list" }));
    }
  },
  validations: {
    profile: {
      name: { required, minLength: minLength(5) },
      gender: { required },
      dob: { required },
      country: { required },
      state: { required },
      city: { required, minLength: minLength(3) },
      madhab: { required },
      maritalStatus: { required },
      legalStatus: { required },
      ethnicity: { required },
      relationToUser: { required },
    },
    relationOther: {
      required(value, vm) {
        return vm.profile.relationToUser !== "Other" || !!value;
      },
    },
  },
  computed: {
    ageColor() {
      if (this.age === "") return "grey--text text--darken-1";
      return this.age < 17
        ? "red--text text--darken-2"
        : "green--text text--darken-2";
    },
    age() {
      if (!this.dobMonth || !this.dobDay || !this.dobYear) return "";
      const today = new Date();
      const birth = new Date(this.dobYear, this.dobMonth - 1, this.dobDay);
      let a = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) a--;
      return a;
    },
    progressColor() {
      if (this.completionPercent < 30) return "error";
      if (this.completionPercent < 70) return "warning";
      return "success";
    },
    progressMessage() {
      if (this.completionPercent < 20)
        return "😅 You’ve barely started — good luck finding a match like this!";
      if (this.completionPercent < 40)
        return "🧐 Add a few more details — we’re still guessing who you are.";
      if (this.completionPercent < 60)
        return "🙂 You’re halfway there — matches get better with details!";
      if (this.completionPercent < 80)
        return "👍 Great! Just a few more questions for perfect matches.";
      if (this.completionPercent < 100)
        return "✨ Almost done — your profile looks strong!";
      return "💯 Excellent! You’ve maximized your chances for great matches!";
    },
  },
  methods: {
    relationErrors() {
      const errors = [];
      if (!this.$v.profile.relationToUser.$dirty) return errors;
      if (!this.$v.profile.relationToUser.required)
        errors.push("Please select your relationship to this person");
      return errors;
    },
    relationOtherErrors() {
      const errors = [];
      if (!this.$v.relationOther.$dirty) return errors;
      if (!this.$v.relationOther.required)
        errors.push("Please specify your relationship");
      return errors;
    },
    cityErrors() {
      const errors = [];
      if (!this.$v.profile.city.$dirty) return errors;
      if (!this.$v.profile.city.required) errors.push("City is required");
      if (!this.$v.profile.city.minLength)
        errors.push("City must be at least 3 characters");
      return errors;
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.profile.name.$dirty) return errors;
      if (!this.$v.profile.name.required) errors.push("Name is required");
      if (!this.$v.profile.name.minLength)
        errors.push("Name must be at least 5 characters");
      return errors;
    },
    genderErrors() {
      const errors = [];
      if (!this.$v.profile.gender.$dirty) return errors;
      if (!this.$v.profile.gender.required)
        errors.push("Please select your gender");
      return errors;
    },
    dobErrors() {
      const errors = [];
      if (!this.$v.profile.dob.$dirty) return errors;
      if (!this.$v.profile.dob.required) {
        errors.push("Date of Birth is required");
      } else if (this.age !== "" && this.age < 17) {
        errors.push("Minimum marriage age is 17 years old");
      }
      return errors;
    },
    countryErrors() {
      const errors = [];
      if (!this.$v.profile.country.$dirty) return errors;
      if (!this.$v.profile.country.required) errors.push("Country is required");
      return errors;
    },
    stateErrors() {
      const errors = [];
      if (!this.$v.profile.state.$dirty) return errors;
      if (!this.$v.profile.state.required) errors.push("State is required");
      return errors;
    },
    handleGenderChange(reset = true) {
      this.appearanceOptions =
        this.appearanceOptionsByGender[this.profile.gender] || [];
      if (reset) this.profile.appearance = "";
      this.updateProgress();
    },
    handleCountryChange(reset = true) {
      this.availableStates = this.statesByCountry[this.profile.country] || [];
      if (reset) this.profile.state = "";
      this.updateProgress();
    },
    updateDob() {
      if (this.dobMonth && this.dobDay && this.dobYear) {
        const m = this.dobMonth.toString().padStart(2, "0");
        const d = this.dobDay.toString().padStart(2, "0");
        const y = this.dobYear;
        this.profile.dob = `${y}-${m}-${d}`;
        this.updateProgress();
      }
    },
    updateProgress() {
      if (!this.profile || typeof this.profile !== "object") {
        this.completionPercent = 0;
        return;
      }
      const answered = Object.values(this.profile).filter(
        (v) => v !== "" && v !== null && v !== false
      ).length;
      this.completionPercent = Math.min(
        Math.round((answered / this.totalFields) * 100),
        100
      );
    },
    async onFinish() {
      this.$v.$touch();
      if (this.$v.$invalid || (this.age && this.age < 17)) {
        this.$swal({
          icon: "error",
          title: "Please complete required fields before saving",
          toast: true,
          position: "top-end",
          timer: 3000,
          showConfirmButton: false,
        });
        return;
      }
      await this.updateProfile();
    },
    async updateProfile() {
      this.saving = true;
      try {
        const result = await profileService.update(
          this.profile.id,
          this.profile
        );
        if (result) {
          this.submitted = true;
        }
      } catch (err) {
        console.error(err);
        this.$swal({
          icon: "error",
          title: "Unable to update profile",
          text:
            (err &&
              err.response &&
              err.response.data &&
              err.response.data.error) ||
            "Something went wrong",
        });
      } finally {
        this.saving = false;
      }
    },
    canProceedStep(stepNum) {
      // Minimal blocking fields per step
      if (stepNum === 1) {
        // Basic Info
        return (
          this.$v.profile.name.required &&
          this.$v.profile.name.minLength &&
          this.$v.profile.gender.required &&
          this.$v.profile.dob.required &&
          (this.age === "" || this.age >= 17) &&
          this.$v.profile.country.required &&
          this.$v.profile.state.required &&
          this.$v.profile.city.required &&
          this.$v.profile.city.minLength &&
          this.$v.relationOther.required
        );
      }
      if (stepNum === 2) {
        // Personal
        return (
          this.$v.profile.maritalStatus.required &&
          this.$v.profile.legalStatus.required
        );
      }
      if (stepNum === 3) {
        // Faith
        return this.$v.profile.madhab.required;
      }
      if (stepNum === 4) {
        // Family
        return this.$v.profile.ethnicity.required;
      }
      if (stepNum === 5) {
        // Lifestyle (no hard required fields)
        return true;
      }
      if (stepNum === 6) {
        // Career (no hard required fields)
        return true;
      }
      if (stepNum === 7) {
        // Career (no hard required fields)
        return true;
      }
      return true;
    },
    nextStep() {
      if (this.step < this.totalSteps) {
        // touch the fields of current step to show messages if missing
        this.touchStep(this.step);
        if (!this.canProceedStep(this.step)) return;
        this.step += 1;
        // scroll to top of the container for mobile comfort
        window.scrollTo(0, 0);
      }
    },
    prevStep() {
      if (this.step > 1) {
        this.step -= 1;
        window.scrollTo(0, 0);
      }
    },
    touchStep(stepNum) {
      if (stepNum === 1) {
        this.$v.profile.name.$touch();
        this.$v.profile.gender.$touch();
        this.$v.profile.dob.$touch();
        this.$v.profile.country.$touch();
        this.$v.profile.state.$touch();
        this.$v.profile.city.$touch();
      } else if (stepNum === 2) {
        this.$v.profile.maritalStatus.$touch();
        this.$v.profile.legalStatus.$touch();
      } else if (stepNum === 3) {
        this.$v.profile.madhab.$touch();
      } else if (stepNum === 4) {
        this.$v.profile.ethnicity.$touch();
      }
    },
  },
};
</script>

<style scoped>
.progress-bar-floating {
  position: fixed;
  top: 64px;
  left: 0;
  right: 0;
  z-index: 10;
  padding: 8px 16px;
}
.glass-bar {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
}
.stepper-nonclickable .v-stepper__step {
  pointer-events: none;
}
.stepper-nonclickable .v-stepper__header {
  background: transparent !important;
  box-shadow: none !important;
}
</style>